import * as Immutable from "immutable";
import { DrumData, EmptyNote, Note } from "./data";
import { GrooveData } from "./grooveUtils";
import {
  isTripletDivision,
  isTripletDivisionFromNotesPerMeasure,
  NUMBER_OF_TOMS,
} from "./utils";

export const ABC_OFF = false;
export type ABC_OFF = typeof ABC_OFF;

export enum StickAbcSymbol {
  R = '"R"x',
  L = '"L"x',
  BOTH = '"R/L"x',
  COUNT = '"count"x',
  OFF = '""x',
}

export enum HHAbcSymbol {
  Ride = "^A'",
  Ride_Bell = "^B'",
  Cow_Bell = "^D'",
  Crash = "^c'",
  Stacker = "^d'",
  Open = "!open!^g",
  Close = "!plus!^g",
  Accent = "!accent!^g",
  Normal = "^g",
}

export enum SnareAbcSymbol {
  Ghost = "!(.!!).!c",
  Accent = "!accent!c",
  Normal = "c",
  XStick = "^c",
  Buzz = "!///!c",
  Flam = "{/c}c",
  Drag = "{/cc}c",
}

export enum KickAbcSymbol {
  SandK = "[F^d,]", // kick & splash
  Splash = "^d,", // splash only
  Normal = "F",
}

export enum TomAbcSymbol {
  T1 = "e",
  T2 = "d",
  T3 = "B",
  T4 = "A",
}

export type AbcSymbol =
  | StickAbcSymbol
  | HHAbcSymbol
  | SnareAbcSymbol
  | KickAbcSymbol
  | TomAbcSymbol;

export type AbcNote = AbcSymbol | typeof ABC_OFF;

// the top stuff in the ABC that doesn't depend on the notes
function getTopAbcBoilerplate(
  isPermutation: boolean,
  tuneTitle: string,
  tuneAuthor: string,
  tuneComments: string,
  showLegend: boolean,
  isTriplets: boolean,
  kick_stems_up: boolean,
  timeSigTop: number,
  timeSigBottom: number,
  renderWidth: number,
): string {
  // boiler plate
  // let fullABC = "%abc\n%%fullsvg _" + grooveUtilsUniqueIndex + "\nX:6\n";
  let fullABC = "%abc\n%%fullsvg _1\nX:6\n";

  fullABC += `M:${timeSigTop}/${timeSigBottom}\n`;

  // always add a Title even if it's blank
  fullABC += `T: ${tuneTitle}\n`;

  if (tuneAuthor !== "") {
    fullABC += `C: ${tuneAuthor}\n`;
    fullABC += "%%musicspace 20px\n"; // add some more space
  }

  // TODO, figure out if this is a hard limit or not. remove it if so
  if (renderWidth < 400) {
    renderWidth = 400;
  } // min-width
  if (renderWidth > 3000) {
    renderWidth = 3000;
  } // max-width
  // the width of the music is always 25% bigger than what we pass in.   Go figure.
  renderWidth = Math.floor(renderWidth * 0.75);

  fullABC += `L:1/${32}\n`; // 4/4 = 32,  6/8 = 64

  if (isPermutation) {
    fullABC += "%%stretchlast 0\n";
  } else {
    fullABC += "%%stretchlast 1\n";
  }

  fullABC += `%%flatbeams 1
%%ornament up
%%pagewidth ${renderWidth}px
%%leftmargin 0cm
%%rightmargin 0cm
%%topspace 10px
%%titlefont calibri 20
%%partsfont calibri 16
%%gchordfont calibri 16
%%annotationfont calibri 16
%%infofont calibri 16
%%textfont calibri 16
%%deco (. 0 a 5 1 1 "@-8,-3("
%%deco ). 0 a 5 1 1 "@4,-3)"
%%beginsvg
  <defs>
    <path id="Xhead" d="m-3,-3 l6,6 m0,-6 l-6,6" class="stroke" style="stroke-width:1.2"/>
    <path id="Trihead" d="m-3,2 l 6,0 l-3,-6 l-3,6 l6,0" class="stroke" style="stroke-width:1.2"/>
  </defs>
%%endsvg
%%map drum ${HHAbcSymbol.Normal} heads=Xhead print=${HHAbcSymbol.Normal.slice(
    1,
  )}     % Hi-Hat
%%map drum ${HHAbcSymbol.Crash} heads=Xhead print=${HHAbcSymbol.Crash.slice(
    1,
  )}   % Crash
%%map drum ${HHAbcSymbol.Stacker} heads=Xhead print=${HHAbcSymbol.Stacker.slice(
    1,
  )}   % Stacker
%%map drum ${HHAbcSymbol.Ride} heads=Xhead print=${HHAbcSymbol.Ride.slice(
    1,
  )}   % Ride
%%map drum ${
    HHAbcSymbol.Ride_Bell
  } heads=Trihead print=${HHAbcSymbol.Ride_Bell.slice(1)} % Ride Bell
%%map drum ${
    HHAbcSymbol.Cow_Bell
  } heads=Trihead print=${HHAbcSymbol.Cow_Bell.slice(1)} % Cow Bell
%%map drum ${
    SnareAbcSymbol.XStick
  } heads=Xhead print=${SnareAbcSymbol.XStick.slice(1)} % Cross Stick
%%map drum ${
    KickAbcSymbol.Splash
  } heads=Xhead print=${KickAbcSymbol.Splash.slice(1)}   % Foot Splash
`;

  //if(kick_stems_up)
  //fullABC += "%%staves (Stickings Hands)\n";
  //else
  fullABC += "%%staves (Stickings Hands Feet)\n";

  // print comments below the legend if there is one, otherwise in the header section
  if (tuneComments !== "") {
    fullABC += `P: ${tuneComments}\n`;
    fullABC += "%%musicspace 20px\n"; // add some more space
  }

  // the K ends the header;
  fullABC += "K:C clef=perc\n";

  if (showLegend) {
    fullABC += `V:Stickings
x8 x8 x8 x8 x8 x8 x8 x8 ||
V:Hands stem=up 
%%voicemap drum
"^Hi-Hat"^g4 "^Open"!open!^g4 "^Crash"^c\'4 "^Stacker"^d\'4 "^Ride"^A\'4 "^Ride Bell"^B\'4 x2 "^Tom"e4 "^Tom"A4 "^Snare"c4 "^Buzz"!///!c4 "^Cross"^c4 "^Ghost  "!(.!!).!c4 "^Flam"{/c}c4  x10 ||
V:Feet stem=down 
%%voicemap drum
x52 "^Kick"F4 "^HH foot"^d,4 x4 ||
T:`;
  }

  // tempo setting
  // fullABC += "Q: 1/4=" + getTempo() + "\n";

  return fullABC;
}

// looks for modifiers like !accent! or !plus! and moves them outside of the group abc array.
// Most modifiers (but not all) will not render correctly if they are inside the abc group.
// returns a string that should be added to the abc_notation if found.
function moveAccentsOrOtherModifiersOutsideOfGroup(
  abcNoteStrings: {
    notes1: string;
    notes2: string;
    notes3: string;
  },
  searchModifier: string,
): string {
  let found_modifier = false;
  let rindex = abcNoteStrings.notes1.lastIndexOf(searchModifier);
  if (rindex > -1) {
    found_modifier = true;
    abcNoteStrings.notes1 = abcNoteStrings.notes1.replace(searchModifier, "");
  }
  rindex = abcNoteStrings.notes2.lastIndexOf(searchModifier);
  if (rindex > -1) {
    found_modifier = true;
    abcNoteStrings.notes2 = abcNoteStrings.notes2.replace(searchModifier, "");
  }
  rindex = abcNoteStrings.notes3.lastIndexOf(searchModifier);
  if (rindex > -1) {
    found_modifier = true;
    abcNoteStrings.notes3 = abcNoteStrings.notes3.replace(searchModifier, "");
  }
  if (found_modifier) {
    return searchModifier;
  }

  return ""; // didn't find it so return nothing
}

// take an array of arrays and use a for loop to test to see
// if all of the arrays are equal to the "test_value" for a given "test_index"
// returns "true" if they are all equal.
// returns "false" if any one of them fails
function testArrayOfArraysForEquality<T>(
  array_of_arrays: Array<Array<T>>,
  test_index: number,
  test_value: T,
): boolean {
  for (const array of array_of_arrays) {
    if (array[test_index] !== undefined && array[test_index] !== test_value) {
      return false;
    }
  }

  return true;
}

// note1_array:   an array containing ABC_OFF or a note character in ABC to designate that is is on
// note2_array:   an array containing ABC_OFF or a note character in ABC to designate that is is on
// end_of_group:  when to stop looking ahead in the array.  (since we group notes in to beats)
function getABCforNote(
  note_array_of_arrays: Array<Array<AbcNote>>,
  start_index: number,
  end_of_group: number,
  scaler: number,
): string {
  let ABC_String = "";
  const abcNoteStrings = {
    notes1: "",
    notes2: "",
    notes3: "",
  };
  let num_notes_on = 0;
  let nextCount;

  for (const note_array of note_array_of_arrays) {
    if (
      note_array[start_index] !== undefined &&
      note_array[start_index] !== ABC_OFF
    ) {
      // look ahead and see when the next note is
      // the length of this note is dependant on when the next note lands
      // for every empty space we increment nextCount, and then make the note that long
      nextCount = 1;
      for (
        let indexA = start_index + 1;
        indexA < start_index + end_of_group;
        indexA++
      ) {
        if (
          !testArrayOfArraysForEquality(note_array_of_arrays, indexA, ABC_OFF)
        ) {
          break;
        } else {
          nextCount++;
        }
      }

      abcNoteStrings.notes1 +=
        (note_array[start_index] as any) + scaler * nextCount;
      num_notes_on++;
    }
  }

  if (num_notes_on > 1) {
    // if multiple are on, we need to combine them with []
    // horrible hack.  Turns out ABC will render the accents wrong unless the are outside the brackets []
    // look for any accents that are delimited by "!"  (eg !accent!  or !plus!)
    // move the accents to the front
    ABC_String += moveAccentsOrOtherModifiersOutsideOfGroup(
      abcNoteStrings,
      "!accent!",
    );
    // in case there are two accents (on both snare and hi-hat) we remove the second one
    moveAccentsOrOtherModifiersOutsideOfGroup(abcNoteStrings, "!accent!");
    ABC_String += moveAccentsOrOtherModifiersOutsideOfGroup(
      abcNoteStrings,
      "!plus!",
    );
    ABC_String += moveAccentsOrOtherModifiersOutsideOfGroup(
      abcNoteStrings,
      "!open!",
    );
    ABC_String += moveAccentsOrOtherModifiersOutsideOfGroup(
      abcNoteStrings,
      "!///!",
    );

    // Look for '[' and ']'.   They are added on to the the kick and splash and could be added to other notes
    // in the future.   They imply that the notes are on the same beat.   Since we are already putting multiple
    // notes on the same beat (see code below this line that adds '[' & ']'), we need to remove them or the
    // resulting ABC will be invalid
    moveAccentsOrOtherModifiersOutsideOfGroup(abcNoteStrings, "[");
    moveAccentsOrOtherModifiersOutsideOfGroup(abcNoteStrings, "]");

    // this is the flam notation, it can't be in a sub grouping
    ABC_String += moveAccentsOrOtherModifiersOutsideOfGroup(
      abcNoteStrings,
      "{/c}",
    );
    // this is the drag notation, it can't be in a sub grouping
    ABC_String += moveAccentsOrOtherModifiersOutsideOfGroup(
      abcNoteStrings,
      "{/cc}",
    );

    ABC_String += `[${abcNoteStrings.notes1}${abcNoteStrings.notes2}${
      abcNoteStrings.notes3
    }]`; // [^gc]
  } else {
    ABC_String +=
      abcNoteStrings.notes1 + abcNoteStrings.notes2 + abcNoteStrings.notes3; // note this could be a noOp if all strings are blank
  }

  return ABC_String;
}

// calculate the rest ABC string
function getABCforRest(
  note_array_of_arrays: Array<Array<AbcNote>>,
  start_index: number,
  end_of_group: number,
  scaler: number,
  use_hidden_rest: boolean,
): string {
  let ABC_String = "";

  // count the # of rest
  if (
    testArrayOfArraysForEquality(note_array_of_arrays, start_index, ABC_OFF)
  ) {
    let restCount = 1;
    for (
      let indexB = start_index + 1;
      indexB < start_index + end_of_group;
      indexB++
    ) {
      if (
        !testArrayOfArraysForEquality(note_array_of_arrays, indexB, ABC_OFF)
      ) {
        break;
      } else {
        restCount++;
      }
    }

    // now output a rest for the duration of the rest count
    if (use_hidden_rest) {
      ABC_String += `x${scaler * restCount}`;
    } else {
      ABC_String += `z${scaler * restCount}`;
    }
  }

  return ABC_String;
}

// function to return 1,e,&,a or 2,3,4,5,6, etc...
export function figure_out_sticking_count_for_index(
  index: number,
  notes_per_measure: number,
  sub_division: number,
  time_sig_bottom: number,
): number | string {
  // figure out the count state by looking at the id and the subdivision
  const note_index = index % notes_per_measure;
  // 4/2 time changes the implied time from 4 up to 8, etc
  // 6/8 time changes the implied time from 8 down to 4
  const implied_sub_division = sub_division * (4 / time_sig_bottom);
  switch (implied_sub_division) {
    case 4:
      return note_index + 1; // 1,2,3,4,5, etc.
    case 8:
      return note_index % 2 === 0 ? Math.floor(note_index / 2) + 1 : "&";
    case 12: // 8th triplets
      if (note_index % 3 === 0) {
        return Math.floor(note_index / 3) + 1;
      } else if (note_index % 3 === 1) {
        // 1,2,3,4,5, etc.
        return "&";
      } else {
        return "a";
      }
    case 24: // 16th triplets
      if (note_index % 3 === 0) {
        return Math.floor(note_index / 6) + 1;
      } else if (note_index % 3 === 1) {
        // 1,2,3,4,5, etc.
        return "&";
      } else {
        return "a";
      }
    case 48: // 32nd triplets
      if (note_index % 3 === 0) {
        return Math.floor(note_index / 12) + 1;
      } else if (note_index % 3 === 1) {
        // 1,2,3,4,5, etc.
        return "&";
      } else {
        return "a";
      }
    case 16:
    case 32: // fall through
    default:
      const whole_note_interval = implied_sub_division / 4;
      if (note_index % 4 === 0) {
        return Math.floor(note_index / whole_note_interval) + 1;
      } else if (note_index % 4 === 1) {
        // 1,1,2,2,3,3,4,4,5,5, etc.
        return "e";
      } else if (note_index % 4 === 2) {
        return "&";
      } else {
        return "a";
      }
  }
}

// when we generate ABC we use a default larger note array and transpose it
// For 8th note triplets that means we need to use a larger grouping to make it
// scale correctly
// The base array is now 32 notes long to support 32nd notes
// since we would normally group by 4 we need to group by 8 since we are scaling it
function calcAbcNoteGroupingSize(
  usingTriplets: boolean,
  timeSigTop: number,
  timeSigBottom: number,
): number {
  if (usingTriplets) {
    return 12;
  }

  // 3/4, 3/8, 3/16
  if (timeSigTop === 3) {
    return 8 * (4 / timeSigBottom);
  }

  // 3/4, 6/8, 9/8, 12/8
  if (timeSigTop % 6 === 0 && timeSigBottom % 8 === 0) {
    return 12 * (8 / timeSigBottom);
  }

  // return 8 * (4/timeSigBottom);
  return 8;
}

// count the number of note positions that are not rests in all the arrays
// FFFxFFFxF  would be 2
function countActiveNotesInArrays(
  array_of_arrays: Array<Array<AbcNote>>,
  start_index: number,
  how_far_to_measure: number,
): number {
  let num_active_notes = 0;

  for (let i = start_index; i < start_index + how_far_to_measure; i++) {
    for (
      let which_array = 0;
      which_array < array_of_arrays.length;
      which_array++
    ) {
      if (array_of_arrays[which_array][i] !== ABC_OFF) {
        num_active_notes++;
        which_array = array_of_arrays.length; // exit this inner for loop immediately
      }
    }
  }

  return num_active_notes;
}

// takes 4 arrays 48 elements long that represent the stickings, snare, HH & kick.
// each element contains either the note value in ABC "F","^g" or false to represent off
// translates them to an ABC string (in 2 voices if !kick_stems_up)
// post_voice_abc is a string added to the end of each voice line that can end the line
//
// We output 48 notes in the ABC rather than the traditional 16 or 32 for 4/4 time.
// This is because of the stickings above the bar are a separate voice and should not have the "3" above them
// This could be changed to using the normal number and moving all the stickings down to be comments on each note in one voice (But is a pretty big change)
function abcForTriplets(
  sticking_array: Array<AbcNote>,
  HH_array: Array<AbcNote>,
  snare_array: Array<AbcNote>,
  kick_array: Array<AbcNote>,
  toms_array: Array<Array<AbcNote>>,
  post_voice_abc: string,
  num_notes: number,
  sub_division: number,
  notes_per_measure: number,
  kick_stems_up: boolean,
  timeSigTop: number,
  timeSigBottom: number,
): string {
  const scaler = 1; // we are always in 48 notes here, and the ABC needs to think we are in 48 since the specified division is 1/32
  let ABC_String = "";
  let stickings_voice_string = "V:Stickings\n";
  let hh_snare_voice_string = "V:Hands stem=up\n%%voicemap drum\n";
  let kick_voice_string = "V:Feet stem=down\n%%voicemap drum\n";
  let all_drum_array_of_array;

  // console.log(HH_array);
  // console.log(kick_array);
  // console.log(notes_per_measure);
  // console.log(sub_division);

  all_drum_array_of_array = kick_stems_up
    ? [snare_array, HH_array, kick_array]
    : [snare_array, HH_array];

  if (toms_array) {
    all_drum_array_of_array = all_drum_array_of_array.concat(toms_array);
  }

  // occationally we will change the sub_division output to 1/8th or 1/16th notes when we detect a beat that is better displayed that way
  // By default we use the base sub_division but this can be set different below
  let faker_sub_division = sub_division;

  for (let i = 0; i < num_notes; i++) {
    // triplets are special.  We want to output a note or a rest for every space of time
    // 8th note triplets should always use rests
    // end_of_group should be
    //  "4" for 1/8th note triplets
    //  "2" for 1/16th note triplets
    //  "1" for 1/32nd note triplets.
    let end_of_group = 48 / faker_sub_division;
    let grouping_size_for_rests = end_of_group;
    let skip_adding_more_notes = false;

    if (i % notes_per_measure + end_of_group > notes_per_measure) {
      // if we are in an odd time signature then the last few notes will have a different grouping to reach the end of the measure
      end_of_group = notes_per_measure - i % num_notes;
    }

    if (i % calcAbcNoteGroupingSize(true, timeSigTop, timeSigBottom) === 0) {
      // Look for some special cases that will format beats as non triplet groups.   Quarter notes, 1/8th and 1/16th notes only.

      // look for a whole beat of rests
      if (0 === countActiveNotesInArrays(all_drum_array_of_array, i, 12)) {
        // there are no notes in the next beat.   Let's output a special string for a quarter note rest
        skip_adding_more_notes = true;
        stickings_voice_string += "x8";
        hh_snare_voice_string += "z8"; // quarter note rest
        i += 11; // skip past all the rests

        // look for 1/4 note with no triplets  "x--"
      } else if (
        0 === countActiveNotesInArrays(all_drum_array_of_array, i + 1, 11)
      ) {
        // code duplicated from below
        // clear any invalid stickings since they will mess up the formatting greatly
        for (let si = i + 1; si < i + 12; si++) {
          sticking_array[si] = false;
        }
        stickings_voice_string += getABCforRest(
          [sticking_array],
          i,
          8,
          scaler,
          true,
        );
        stickings_voice_string += getABCforNote([sticking_array], i, 8, scaler);

        if (kick_stems_up) {
          hh_snare_voice_string += getABCforNote(
            all_drum_array_of_array,
            i,
            8,
            scaler,
          );
          kick_voice_string = "";
        } else {
          hh_snare_voice_string += getABCforNote(
            all_drum_array_of_array,
            i,
            8,
            scaler,
          );
          kick_voice_string += getABCforNote([kick_array], i, 8, scaler);
        }

        skip_adding_more_notes = true;
        i += 11; // skip past to the next beat

        // look for two 1/8 notes with no triplets in 1/16th & 1/32nd note triplets.   "x--x--", "x-----x-----"
      } else if (
        sub_division > 12 &&
        0 === countActiveNotesInArrays(all_drum_array_of_array, i + 1, 5) &&
        0 === countActiveNotesInArrays(all_drum_array_of_array, i + 7, 5)
      ) {
        // think of the 1/8 notes as two groups of 3 notes
        for (let eighth_index = i; eighth_index <= i + 6; eighth_index += 6) {
          // code duplicated from below
          // clear any invalid stickings since they will mess up the formatting greatly
          for (let si = eighth_index + 1; si < eighth_index + 6; si++) {
            sticking_array[si] = false;
          }
          stickings_voice_string += getABCforRest(
            [sticking_array],
            eighth_index,
            4,
            scaler,
            true,
          );
          stickings_voice_string += getABCforNote(
            [sticking_array],
            eighth_index,
            4,
            scaler,
          );

          if (kick_stems_up) {
            hh_snare_voice_string += getABCforRest(
              all_drum_array_of_array,
              eighth_index,
              4,
              scaler,
              false,
            );
            hh_snare_voice_string += getABCforNote(
              all_drum_array_of_array,
              eighth_index,
              4,
              scaler,
            );
            kick_voice_string = "";
          } else {
            hh_snare_voice_string += getABCforRest(
              all_drum_array_of_array,
              eighth_index,
              4,
              scaler,
              false,
            );
            hh_snare_voice_string += getABCforNote(
              all_drum_array_of_array,
              eighth_index,
              4,
              scaler,
            );
            kick_voice_string += getABCforNote(
              [kick_array],
              eighth_index,
              4,
              scaler,
            );
          }
        }

        skip_adding_more_notes = true;
        i += 11; // skip past to the next beat

        // look for 1/16th notes with no triplets in 1/32nd note triplets.   "x--x--"
      } else if (
        sub_division === 48 &&
        0 === countActiveNotesInArrays(all_drum_array_of_array, i + 1, 2) &&
        0 === countActiveNotesInArrays(all_drum_array_of_array, i + 4, 2) &&
        0 === countActiveNotesInArrays(all_drum_array_of_array, i + 7, 2) &&
        0 === countActiveNotesInArrays(all_drum_array_of_array, i + 10, 2)
      ) {
        // think of the 1/8 notes as two groups of 3 notes
        for (let eighth_index = i; eighth_index <= i + 9; eighth_index += 3) {
          // code duplicated from below
          // clear any invalid stickings since they will mess up the formatting greatly
          for (let si = eighth_index + 1; si < eighth_index + 3; si++) {
            sticking_array[si] = false;
          }
          stickings_voice_string += getABCforRest(
            [sticking_array],
            eighth_index,
            2,
            scaler,
            true,
          );
          stickings_voice_string += getABCforNote(
            [sticking_array],
            eighth_index,
            2,
            scaler,
          );

          if (kick_stems_up) {
            hh_snare_voice_string += getABCforRest(
              all_drum_array_of_array,
              eighth_index,
              2,
              scaler,
              false,
            );
            hh_snare_voice_string += getABCforNote(
              all_drum_array_of_array,
              eighth_index,
              2,
              scaler,
            );
            kick_voice_string = "";
          } else {
            hh_snare_voice_string += getABCforRest(
              all_drum_array_of_array,
              eighth_index,
              2,
              scaler,
              false,
            );
            hh_snare_voice_string += getABCforNote(
              all_drum_array_of_array,
              eighth_index,
              2,
              scaler,
            );
            kick_voice_string += getABCforNote(
              [kick_array],
              eighth_index,
              2,
              scaler,
            );
          }
        }

        skip_adding_more_notes = true;
        i += 11; // skip past to the next beat
      } else {
        // the normal case.   We tell ABC that we are using a triplet
        let notes_in_triplet_group = sub_division / 4; // 4 beats

        // look through the notes and see if we should "fake" 1/8 or 1/6th note triplets
        // If the groove can be expressed in "3" or "6" groups it is way easier to read than in a higher "12" group with rests
        // "3" looks like "x---x---x---"   one note and three rests
        // "6" looks like "x-x-x-x-x-x-"   one note and one rest
        if (sub_division === 48) {
          let can_fake_threes = true;
          let can_fake_sixes = true;
          for (let j = i; j < i + 12; j += 4) {
            if (
              0 < countActiveNotesInArrays(all_drum_array_of_array, j + 1, 3)
            ) {
              can_fake_threes = false;
            }
            if (
              0 < countActiveNotesInArrays(all_drum_array_of_array, j + 1, 1) ||
              0 < countActiveNotesInArrays(all_drum_array_of_array, j + 3, 1)
            ) {
              can_fake_sixes = false;
            }
            if (can_fake_threes === false && can_fake_sixes === false) {
              break;
            } // skip the rest, since we have an answer already
          }

          // reset

          if (can_fake_threes) {
            faker_sub_division = 12;
          } else if (can_fake_sixes) {
            faker_sub_division = 24;
          } else {
            faker_sub_division = sub_division;
          } // reset

          end_of_group = 48 / faker_sub_division;
          grouping_size_for_rests = end_of_group;
          notes_in_triplet_group = faker_sub_division / 4; // 4 beats
        }

        // creates the 3, 6 or 12 over the note grouping
        // looks like (3:3:3 or (6:6:6 or (12:12:12
        hh_snare_voice_string += `(${notes_in_triplet_group}:${notes_in_triplet_group}:${notes_in_triplet_group}`;
      }
    }

    // skip the code to add notes
    // Happens for special_rest when there are no notes for the next whole beat.
    // Happens when we found only a 1/4 or 1/8 note instead of triplets
    if (!skip_adding_more_notes) {
      if (i % grouping_size_for_rests === 0) {
        // we will output a rest for each place there could be a note
        stickings_voice_string += getABCforRest(
          [sticking_array],
          i,
          grouping_size_for_rests,
          scaler,
          true,
        );

        if (kick_stems_up) {
          hh_snare_voice_string += getABCforRest(
            all_drum_array_of_array,
            i,
            grouping_size_for_rests,
            scaler,
            false,
          );
          kick_voice_string = "";
        } else {
          hh_snare_voice_string += getABCforRest(
            all_drum_array_of_array,
            i,
            grouping_size_for_rests,
            scaler,
            false,
          );
          kick_voice_string += getABCforRest(
            [kick_array],
            i,
            grouping_size_for_rests,
            scaler,
            true,
          );
        }
      }

      stickings_voice_string += getABCforNote(
        [sticking_array],
        i,
        end_of_group,
        scaler,
      );

      if (kick_stems_up) {
        hh_snare_voice_string += getABCforNote(
          all_drum_array_of_array,
          i,
          end_of_group,
          scaler,
        );
        kick_voice_string = "";
      } else {
        hh_snare_voice_string += getABCforNote(
          all_drum_array_of_array,
          i,
          end_of_group,
          scaler,
        );
        kick_voice_string += getABCforNote(
          [kick_array],
          i,
          end_of_group,
          scaler,
        );
      }
    }

    if (
      i % calcAbcNoteGroupingSize(true, timeSigTop, timeSigBottom) ===
      calcAbcNoteGroupingSize(true, timeSigTop, timeSigBottom) - 1
    ) {
      stickings_voice_string += " ";
      hh_snare_voice_string += " "; // Add a space to break the bar line every group notes
      kick_voice_string += " ";
    }

    // add a bar line every measure
    if ((i + 1) % (12 * timeSigTop * (4 / timeSigBottom)) === 0) {
      stickings_voice_string += "|";
      hh_snare_voice_string += "|";
      kick_voice_string += "|";

      // add a line break every 2 measures
      if (
        i < num_notes - 1 &&
        (i + 1) % (12 * timeSigTop * (4 / timeSigBottom) * 2) === 0
      ) {
        stickings_voice_string += "\n";
        hh_snare_voice_string += "\n";
        kick_voice_string += "\n";
      }
    }
  }

  if (kick_stems_up) {
    ABC_String +=
      stickings_voice_string +
      post_voice_abc +
      hh_snare_voice_string +
      post_voice_abc;
  } else {
    ABC_String +=
      stickings_voice_string +
      post_voice_abc +
      hh_snare_voice_string +
      post_voice_abc +
      kick_voice_string +
      post_voice_abc;
  }

  return ABC_String;
}

// takes 4 arrays 32 elements long that represent the sticking, snare, HH & kick.
// each element contains either the note value in ABC "F","^g" or false to represent off
// translates them to an ABC string in 3 voices
// post_voice_abc is a string added to the end of each voice line that can end the line
//
function abcForQuads(
  sticking_array: Array<AbcNote>,
  HH_array: Array<AbcNote>,
  snare_array: Array<AbcNote>,
  kick_array: Array<AbcNote>,
  toms_array: Array<Array<AbcNote>>,
  post_voice_abc: string,
  num_notes: number,
  sub_division: number,
  notes_per_measure: number,
  kick_stems_up: boolean,
  timeSigTop: number,
  timeSigBottom: number,
): string {
  const scaler = 1; // we are always in 32ths notes here
  let ABC_String = "";
  let stickings_voice_string = "V:Stickings\n"; // for stickings.  they are all rests with text comments added
  let hh_snare_voice_string = "V:Hands stem=up\n%%voicemap drum\n"; // for hh and snare
  let kick_voice_string = "V:Feet stem=down\n%%voicemap drum\n"; // for kick drum
  let all_drum_array_of_array;

  all_drum_array_of_array = kick_stems_up
    ? [snare_array, HH_array, kick_array]
    : [snare_array, HH_array];

  if (toms_array) {
    all_drum_array_of_array = all_drum_array_of_array.concat(toms_array);
  }

  for (let i = 0; i < num_notes; i++) {
    let grouping_size_for_rests = calcAbcNoteGroupingSize(
      false,
      timeSigTop,
      timeSigBottom,
    );
    // make sure the group end doesn't go beyond the measure.   Happens in odd time sigs
    if (i % notes_per_measure + grouping_size_for_rests > notes_per_measure) {
      // if we are in an odd time signature then the last few notes will have a different grouping to reach the end of the measure
      grouping_size_for_rests = notes_per_measure - i % notes_per_measure;
    }

    let end_of_group;
    if (i % calcAbcNoteGroupingSize(false, timeSigTop, timeSigBottom) === 0) {
      end_of_group = calcAbcNoteGroupingSize(false, timeSigTop, timeSigBottom);
    } else {
      end_of_group =
        calcAbcNoteGroupingSize(false, timeSigTop, timeSigBottom) -
        i % calcAbcNoteGroupingSize(false, timeSigTop, timeSigBottom);
    }

    // make sure the group end doesn't go beyond the measure.   Happens in odd time sigs
    if (i % notes_per_measure + end_of_group > notes_per_measure) {
      // if we are in an odd time signature then the last few notes will have a different grouping to reach the end of the measure
      end_of_group = notes_per_measure - i % notes_per_measure;
    }

    if (i % calcAbcNoteGroupingSize(false, timeSigTop, timeSigBottom) === 0) {
      // we will only output a rest at the beginning of a beat phrase
      stickings_voice_string += getABCforRest(
        [sticking_array],
        i,
        grouping_size_for_rests,
        scaler,
        true,
      );

      if (kick_stems_up) {
        hh_snare_voice_string += getABCforRest(
          all_drum_array_of_array,
          i,
          grouping_size_for_rests,
          scaler,
          false,
        );
        kick_voice_string = "";
      } else {
        hh_snare_voice_string += getABCforRest(
          all_drum_array_of_array,
          i,
          grouping_size_for_rests,
          scaler,
          false,
        );
        kick_voice_string += getABCforRest(
          [kick_array],
          i,
          grouping_size_for_rests,
          scaler,
          false,
        );
      }
    }

    stickings_voice_string += getABCforNote(
      [sticking_array],
      i,
      end_of_group,
      scaler,
    );

    if (kick_stems_up) {
      hh_snare_voice_string += getABCforNote(
        all_drum_array_of_array,
        i,
        end_of_group,
        scaler,
      );
      kick_voice_string = "";
    } else {
      hh_snare_voice_string += getABCforNote(
        all_drum_array_of_array,
        i,
        end_of_group,
        scaler,
      );
      kick_voice_string += getABCforNote([kick_array], i, end_of_group, scaler);
    }

    if (
      i % calcAbcNoteGroupingSize(false, timeSigTop, timeSigBottom) ===
      calcAbcNoteGroupingSize(false, timeSigTop, timeSigBottom) - 1
    ) {
      stickings_voice_string += " ";
      hh_snare_voice_string += " "; // Add a space to break the bar line every group notes
      kick_voice_string += " ";
    }

    // add a bar line every meausre.   32 notes in 4/4 time.   (32/timeSigBottom * timeSigTop)
    if ((i + 1) % (32 / timeSigBottom * timeSigTop) === 0) {
      stickings_voice_string += "|";
      hh_snare_voice_string += "|";
      kick_voice_string += "|";
    }
    // add a line break every 2 measures, except the last
    if (
      i < num_notes - 1 &&
      (i + 1) % (32 / timeSigBottom * timeSigTop * 2) === 0
    ) {
      stickings_voice_string += "\n";
      hh_snare_voice_string += "\n";
      kick_voice_string += "\n";
    }
  }

  if (kick_stems_up) {
    ABC_String +=
      stickings_voice_string +
      post_voice_abc +
      hh_snare_voice_string +
      post_voice_abc;
  } else {
    ABC_String +=
      stickings_voice_string +
      post_voice_abc +
      hh_snare_voice_string +
      post_voice_abc +
      kick_voice_string +
      post_voice_abc;
  }

  return ABC_String;
}

// converts the symbol for a sticking count to an actual count based on the time signature
function convertStickingCountsToActualCounts(
  sticking_array: Array<AbcNote>,
  time_division: number,
  timeSigTop: number,
  timeSigBottom: number,
): Array<AbcNote> {
  const cur_div_of_array = isTripletDivision(time_division) ? 48 : 32;

  // the notes per measure is calculated from the note division and the time signature
  // in 4/4 time the division is the division (as well as any time signature x/x)
  // in 4/8 the num notes is half as many, etc
  const actual_notes_per_measure_in_this_array =
    cur_div_of_array / timeSigBottom * timeSigTop;

  // Time division is 4, 8, 16, 32, 12, 24, or 48
  const notes_per_measure_in_time_division =
    time_division / 4 * timeSigTop * (4 / timeSigBottom);

  return sticking_array.map((symbol, i) => {
    if (symbol === StickAbcSymbol.COUNT) {
      // convert the COUNT into an actual letter or number
      // convert the index into what it would have been if the array was "notes_per_measure" sized
      const adjusted_index = Math.floor(
        i /
          (actual_notes_per_measure_in_this_array /
            notes_per_measure_in_time_division),
      );
      const new_count = figure_out_sticking_count_for_index(
        adjusted_index,
        notes_per_measure_in_time_division,
        time_division,
        timeSigBottom,
      );
      return `"${new_count}"x` as AbcNote;
    } else {
      return symbol;
    }
  });
}

// create ABC from note arrays
// The Arrays passed in must be 32 or 48 notes long
// notes_per_measure denotes the number of notes that _should_ be in the measure even though the arrays are always scaled up and large (48 or 32)
function getAbcFromExpandedData(
  sticking_array: Array<AbcNote>,
  HH_array: Array<AbcNote>,
  snare_array: Array<AbcNote>,
  kick_array: Array<AbcNote>,
  toms_array: Array<Array<AbcNote>>,
  post_voice_abc: string,
  num_notes: number,
  time_division: number,
  notes_per_measure: number,
  kick_stems_up: boolean,
  timeSigTop: number,
  timeSigBottom: number,
): string {
  const converted_sticking_array = convertStickingCountsToActualCounts(
    sticking_array,
    time_division,
    timeSigTop,
    timeSigBottom,
  );

  if (
    isTripletDivisionFromNotesPerMeasure(
      notes_per_measure,
      timeSigTop,
      timeSigBottom,
    )
  ) {
    return abcForTriplets(
      converted_sticking_array,
      HH_array,
      snare_array,
      kick_array,
      toms_array,
      post_voice_abc,
      num_notes,
      time_division,
      notes_per_measure,
      kick_stems_up,
      timeSigTop,
      timeSigBottom,
    );
  } else {
    return abcForQuads(
      converted_sticking_array,
      HH_array,
      snare_array,
      kick_array,
      toms_array,
      post_voice_abc,
      num_notes,
      time_division,
      notes_per_measure,
      kick_stems_up,
      timeSigTop,
      timeSigBottom,
    );
  }
}

// take any size array and make it larger by padding it with rests in the spaces between
// For triplets, expands to 48 notes per measure
// For non-triplets, expands to 32 notes per measure
export function scaleNoteArrayToFullSize(
  note_array: Array<AbcNote>,
  num_measures: number,
  notes_per_measure: number,
  timeSigTop: number,
  timeSigBottom: number,
): Array<AbcNote> {
  const scaler = getNoteScaler(notes_per_measure, timeSigTop, timeSigBottom); // fill proportionally
  const retArray: Array<AbcNote> = [];

  if (scaler === 1) {
    // no need to expand
    return note_array;
  }

  // preset to false (rest) all entries in the expanded array
  for (let i = 0; i < num_measures * notes_per_measure * scaler; i++) {
    retArray[i] = ABC_OFF;
  }

  // sparsely fill in the return array with data from passed in array
  for (let i = 0; i < num_measures * notes_per_measure; i++) {
    const ret_array_index = i * scaler;

    retArray[ret_array_index] = note_array[i];
  }

  return retArray;
}

// since note values are 16ths or 12ths this corrects for that by multiplying note values
// timeSigTop is the top number in a time signature (4/4, 5/4, 6/8, 7/4, etc)
function getNoteScaler(
  notes_per_measure: number,
  timeSigTop: number,
  timeSigBottom: number,
): number {
  if (!timeSigTop || timeSigTop < 1 || timeSigTop > 36) {
    console.warn("Error in getNoteScaler, out of range", { timeSigTop });
    return 1;
  } else {
    if (
      isTripletDivisionFromNotesPerMeasure(
        notes_per_measure,
        timeSigTop,
        timeSigBottom,
      )
    ) {
      return Math.ceil(
        notesPerMeasureInFullSizeArray(true, timeSigTop, timeSigBottom) /
          notes_per_measure,
      );
    } else {
      return Math.ceil(
        notesPerMeasureInFullSizeArray(false, timeSigTop, timeSigBottom) /
          notes_per_measure,
      );
    }
  }
}

// a full measure will be defined as 8 * timeSigTop. (4 = 32, 5 = 40, 6 = 48, etc.)
// that implies 32nd notes in quarter note beats
// TODO: should we support triplets here?
function notesPerMeasureInFullSizeArray(
  is_triplet_division: boolean,
  timeSigTop: number,
  timeSigBottom: number,
): number {
  if (is_triplet_division) {
    return 48 * (timeSigTop / timeSigBottom);
  } else {
    return 32 * (timeSigTop / timeSigBottom);
  }
}

// take any size array and make it larger by padding it with rests in the spaces between
// For triplets, expands to 48 notes per measure
// For non-triplets, expands to 32 notes per measure
export function scaleNoteListToFullSize(
  note_array: Immutable.List<Note>,
  num_measures: number,
  notes_per_measure: number,
  timeSigTop: number,
  timeSigBottom: number,
): Array<Note> {
  const scaler = getNoteScaler(notes_per_measure, timeSigTop, timeSigBottom); // fill proportionally
  const retArray: Array<Note> = new Array(
    num_measures * notes_per_measure * scaler,
  ).fill(EmptyNote);

  if (scaler === 1) {
    // no need to expand
    return note_array.toJS();
  }

  // sparsely fill in the return array with data from passed in array
  for (let i = 0; i < num_measures * notes_per_measure; i++) {
    const ret_array_index = i * scaler;

    retArray[ret_array_index] = note_array.get(i);
  }

  return retArray;
}

export function createAbcFromData(data: DrumData, renderWidth: number) {
  const instrumentNotes = data.instruments.map((notes, instrument) =>
    scaleNoteListToFullSize(
      notes!,
      data.numberOfMeasures,
      data.notesPerMeasure,
      data.numBeats,
      data.noteValue,
    ).map<AbcNote>(note => {
      const md = instrument!.metadata.find(n => n.name === note);
      if (md) {
        return md.abc as AbcSymbol;
      }
      return ABC_OFF;
    }),
  );

  const is_triplet_division = isTripletDivisionFromNotesPerMeasure(
    data.notesPerMeasure,
    data.numBeats,
    data.noteValue,
  );

  const kickStemsUp = true;
  const showLegend = false;

  return (
    getTopAbcBoilerplate(
      false,
      data.title,
      data.author,
      data.comments,
      showLegend,
      is_triplet_division,
      kickStemsUp,
      data.numBeats,
      data.noteValue,
      renderWidth,
    ) +
    getAbcFromExpandedData(
      instrumentNotes.find((v, instrument) => instrument!.name === "sticking"),
      instrumentNotes.find((v, instrument) => instrument!.name === "hh"),
      instrumentNotes.find((v, instrument) => instrument!.name === "snare"),
      instrumentNotes.find((v, instrument) => instrument!.name === "kick"),
      instrumentNotes.filter((v, instrument) => instrument!.name.includes("Tom ")).toArray(),
      "|\n",
      instrumentNotes.first().length,
      data.timeDivision,
      notesPerMeasureInFullSizeArray(
        is_triplet_division,
        data.numBeats,
        data.noteValue,
      ), // notes_per_measure, We scaled up to 48/32 above
      kickStemsUp,
      data.numBeats,
      data.noteValue,
    )
  );
}

// create ABC notation from a GrooveData class
// returns a string of ABC Notation data
export function createABCFromGrooveData(
  myGrooveData: GrooveData,
  renderWidth: number,
) {
  const FullNoteStickingArray = scaleNoteArrayToFullSize(
    myGrooveData.sticking_array,
    myGrooveData.numberOfMeasures,
    myGrooveData.notesPerMeasure,
    myGrooveData.numBeats,
    myGrooveData.noteValue,
  );
  const FullNoteHHArray = scaleNoteArrayToFullSize(
    myGrooveData.hh_array,
    myGrooveData.numberOfMeasures,
    myGrooveData.notesPerMeasure,
    myGrooveData.numBeats,
    myGrooveData.noteValue,
  );
  const FullNoteSnareArray = scaleNoteArrayToFullSize(
    myGrooveData.snare_array,
    myGrooveData.numberOfMeasures,
    myGrooveData.notesPerMeasure,
    myGrooveData.numBeats,
    myGrooveData.noteValue,
  );
  const FullNoteKickArray = scaleNoteArrayToFullSize(
    myGrooveData.kick_array,
    myGrooveData.numberOfMeasures,
    myGrooveData.notesPerMeasure,
    myGrooveData.numBeats,
    myGrooveData.noteValue,
  );
  const FullNoteTomsArray: Array<Array<AbcNote>> = [];

  for (let i = 0; i < NUMBER_OF_TOMS; i++) {
    FullNoteTomsArray[i] = scaleNoteArrayToFullSize(
      myGrooveData.toms_array[i],
      myGrooveData.numberOfMeasures,
      myGrooveData.notesPerMeasure,
      myGrooveData.numBeats,
      myGrooveData.noteValue,
    );
  }

  const is_triplet_division = isTripletDivisionFromNotesPerMeasure(
    myGrooveData.notesPerMeasure,
    myGrooveData.numBeats,
    myGrooveData.noteValue,
  );

  return (
    getTopAbcBoilerplate(
      false,
      myGrooveData.title,
      myGrooveData.author,
      myGrooveData.comments,
      myGrooveData.showLegend,
      is_triplet_division,
      myGrooveData.kickStemsUp,
      myGrooveData.numBeats,
      myGrooveData.noteValue,
      renderWidth,
    ) +
    getAbcFromExpandedData(
      FullNoteStickingArray,
      FullNoteHHArray,
      FullNoteSnareArray,
      FullNoteKickArray,
      FullNoteTomsArray,
      "|\n",
      FullNoteHHArray.length,
      myGrooveData.timeDivision,
      notesPerMeasureInFullSizeArray(
        is_triplet_division,
        myGrooveData.numBeats,
        myGrooveData.noteValue,
      ), // notes_per_measure, We scaled up to 48/32 above
      myGrooveData.kickStemsUp,
      myGrooveData.numBeats,
      myGrooveData.noteValue,
    )
  );
}



// WEBPACK FOOTER //
// ./src/abc.ts